import type { ReactNode } from "react"
import { useCallback } from "react"
import type { Notification } from "./model"
import { NotificationType } from "./model"
import { useGetNotifications } from "./selectors"
import { Alert } from "@/ui/composed"
import { Button } from "@/ui/components/ui/button"
import { ButtonLink } from "@/base"
import { useResendVerifyEmail } from "@/profile"
import { useT } from "@/i18n"
import useFlashMessages from "@/notification/useFlashMessages"

type NotificationAlert = {
  variant: "info" | "success" | "warning" | "error"
  /** Localised override; the server-rendered text is the fallback */
  title?: string
  action?: ReactNode
}

export default function Notifications() {
  const { t } = useT("notification")
  const { notifications } = useGetNotifications()
  const resendEmail = useResendVerifyEmail()
  useFlashMessages()

  const getProps = useCallback(
    ({ type, entityId, params }: Notification): NotificationAlert => {
      switch (type) {
        case NotificationType.VerifyEmail:
          return {
            variant: "warning",
            action: (
              <Button variant="ghost" onClick={resendEmail}>
                {t("common:resendVerifyEmail")}
              </Button>
            ),
          }
        case NotificationType.EvaluationPublished: {
          const tenantSlug = params?.tenantSlug

          return {
            variant: "info",
            title: params?.respondentEmail
              ? t("evaluationPublished", {
                  respondentEmail: params.respondentEmail,
                })
              : undefined,
            action: tenantSlug ? (
              <ButtonLink
                variant="ghost"
                to="/t/$tenantSlug/evaluations/$assignmentId"
                params={{ tenantSlug, assignmentId: entityId }}
              >
                {t("view")}
              </ButtonLink>
            ) : undefined,
          }
        }
        case NotificationType.EvaluationReady:
          return {
            variant: "info",
            title: params?.tenantName
              ? t("evaluationReady", { tenantName: params.tenantName })
              : undefined,
            action: (
              <ButtonLink
                variant="ghost"
                to="/myprofiles/$assignmentId"
                params={{ assignmentId: entityId }}
              >
                {t("view")}
              </ButtonLink>
            ),
          }
        case NotificationType.SelfProfileReady:
          // entityId is the created self-assignment; jump straight into it.
          return {
            variant: "success",
            title: t("selfProfileReady"),
            action: (
              <ButtonLink
                variant="ghost"
                to="/r/$assignmentId"
                params={{ assignmentId: entityId }}
              >
                {t("start")}
              </ButtonLink>
            ),
          }
        case NotificationType.AssignmentsSent:
          return { variant: "success", title: t("assignmentsSent") }
        case NotificationType.CreditsReady:
          return { variant: "success", title: t("creditsReady") }
      }
    },
    [resendEmail, t],
  )

  if (!notifications?.length) {
    return null
  }

  return (
    <div className="flex flex-col gap-2 p-4">
      {notifications.map((notification) => {
        const { title, ...props } = getProps(notification)

        return (
          <Alert
            key={notification.id}
            {...props}
            title={title ?? notification.text}
          />
        )
      })}
    </div>
  )
}
